// v3.18-1331 — Next Action label derivation for the Pipeline grid.
//
// Replaces the "Best Time to Call" column. Tells the operator the next
// operational step for the deal (e.g. "Call Client", "Follow Up Today",
// "Send Loan Docs") rather than just a preferred call window.
//
// The value is derived from the workflow / state engine whenever
// possible. If no workflow signal applies, the helper falls back to
// existing reminder / task logic to produce a sensible label.
//
// Contract:
//   nextActionForClient(client, opts?) -> { key, label, tone }
//     tone ∈ 'action' | 'wait' | 'schedule' | 'done' | 'default'
//
// This module has no side effects and does not need the API to compute
// the label — every input comes from the client row already fetched by
// the Pipeline grid.

const ACTION = 'action';
const WAIT   = 'wait';
const SCHED  = 'schedule';
const DONE   = 'done';
const DEF    = 'default';

const _norm = (v) => (v || '').toString().trim().toLowerCase();

/** Return today's date at 00:00 in the browser's local tz. */
const _todayStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Truthy if ``at`` (ISO-ish string) is due on/before end of today. */
const _dueToday = (at) => {
  if (!at) return false;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return false;
  const eod = new Date();
  eod.setHours(23, 59, 59, 999);
  return d.getTime() <= eod.getTime();
};

/** Truthy if there is an upcoming (future) appointment. */
const _hasUpcomingAppointment = (client) => {
  const at = client?.next_appointment_at || client?.upcoming_appointment_at
          || client?.appointment_at || null;
  if (!at) return false;
  const d = new Date(at);
  return Number.isFinite(d.getTime()) && d.getTime() >= _todayStart().getTime();
};

/** Truthy if the client has never been contacted. */
const _neverContacted = (client) =>
  !client?.last_contact_at
  && !client?.last_call_at
  && !client?.last_sms_at
  && !client?.last_email_at;

/** Truthy if the reminder/next-action is overdue (past end of today). */
const _isOverdue = (at) => {
  if (!at) return false;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return false;
  const eod = new Date();
  eod.setHours(23, 59, 59, 999);
  return d.getTime() < (new Date()).getTime()
      && d.getTime() < eod.getTime() - 24 * 60 * 60 * 1000;
};

/**
 * v3.18-1332 — canonical Next Action.
 *
 * Understands both the v3.18-1332 canonical Stage vocabulary emitted
 * by the backend Deal Stage Engine AND legacy stage values still
 * present on older deals. Never hardcodes the value list — every
 * branch is derived from actual workflow signals on the row.
 *
 * @param {Object} client
 * @returns {{ key: string, label: string, tone: string }}
 */
export function nextActionForClient(client) {
  if (!client) return { key: 'unknown', label: '—', tone: DEF };

  // v3.21-1639 — Deal Stages are the SSOT.  When the backend workflow
  // resolver has already answered, honor its result verbatim so the
  // Clients table cannot contradict what the Deal Stages checklist
  // shows.  Falls through to the legacy heuristic only for rows the
  // resolver didn't compute (e.g. brand-new leads with no deal_stages
  // doc yet).
  if (client.workflow_next_action_label) {
    return {
      key:   client.workflow_next_action || 'workflow',
      label: client.workflow_next_action_label,
      tone:  client.workflow_branch_state === 'declined' ? DONE : ACTION,
    };
  }
  if (client.workflow_closed === true) {
    return { key: 'complete', label: 'Complete', tone: DONE };
  }

  const stage = _norm(client.deal_stage);
  const status = _norm(client.funding_status);

  // 1) Terminal / completion states — no further action.
  if (stage === 'paid_in_full'
      || ['paid', 'paid_in_full'].includes(status)
      || client.all_parties_settled === true) {
    return { key: 'complete', label: 'Complete', tone: DONE };
  }
  if (['closed', 'completed', 'cancelled'].includes(stage)
      || ['closed', 'completed', 'cancelled', 'declined'].includes(status)) {
    return { key: 'complete', label: 'Complete', tone: DONE };
  }

  // 2) Funding stage — waiting on the bank/lender to release funds.
  //    (Never say "Funding" here — that duplicates the Stage column.)
  if (stage === 'funding'
      || stage === 'funded'
      || ['funded', 'processing', 'ach_in_process'].includes(status)) {
    return { key: 'waiting_on_bank',
             label: 'Waiting on Bank', tone: WAIT };
  }

  // 3) Loan documents.
  if (stage === 'loan_documents_signed' || stage === 'docs_complete') {
    return { key: 'waiting_on_funding',
             label: 'Waiting on Funding', tone: WAIT };
  }
  if (stage === 'loan_documents_sent' || stage === 'docs_sent'
      || stage === 'contract_sent') {
    return { key: 'awaiting_signature',
             label: 'Waiting on Signature', tone: WAIT };
  }

  // 4) Client approved financing → we now owe them loan documents.
  if (stage === 'client_approved'
      || stage === 'financing_approved_by_client'
      || status === 'financing_approved_by_client') {
    return { key: 'send_loan_documents',
             label: 'Send Loan Documents', tone: ACTION };
  }

  // 5) Offers presented — waiting on client's decision.
  if (stage === 'offers_presented' || stage === 'proposal_sent') {
    return { key: 'waiting_on_decision',
             label: 'Waiting on Decision', tone: WAIT };
  }

  // 6) Offers received but not yet presented — present them now.
  if (stage === 'offers_received') {
    return { key: 'present_offers',
             label: 'Present Offers', tone: ACTION };
  }

  // 7) Submitted to lender / underwriting — offers are pending.
  if (stage === 'submitted_to_lenders'
      || stage === 'submitted_to_partner'
      || stage === 'docs_underwriting'
      || stage === 'financing_submitted'
      || stage === 'under_review'
      || status === 'underwriting') {
    return { key: 'waiting_on_offers',
             label: 'Waiting on Offers', tone: WAIT };
  }

  // 8) Application phase.
  if (stage === 'application_complete'
      || stage === 'app_completed') {
    return { key: 'submit_to_lenders',
             label: 'Submit to Lenders', tone: ACTION };
  }
  if (stage === 'application_started' || stage === 'app_sent'
      || stage === 'application') {
    return { key: 'waiting_on_client',
             label: 'Waiting on Client', tone: WAIT };
  }

  // 9) Scheduled appointment overrides "call client" prompts.
  if (_hasUpcomingAppointment(client)) {
    return { key: 'appointment_scheduled',
             label: 'Appointment Scheduled', tone: SCHED };
  }

  // 10) Overdue follow-up — task/reminder past due.
  const dueAt = client.next_task_due_at || client.reminder_due_at
             || client.next_action_due_at;
  if (dueAt && _isOverdue(dueAt)) {
    return { key: 'overdue_follow_up',
             label: 'Overdue Follow-up', tone: ACTION };
  }

  // 11) Task / reminder due today (fallback to existing reminder logic).
  if (_dueToday(dueAt)) {
    return { key: 'follow_up_today',
             label: 'Follow Up Today', tone: ACTION };
  }

  // 12) Brand new lead — never contacted.
  if (_neverContacted(client)) {
    return { key: 'call_client', label: 'Call Client', tone: ACTION };
  }

  // 13) Default fallback.
  return { key: 'follow_up', label: 'Follow Up', tone: DEF };
}


// v3.18-1331 — display tokens for the Rep Assist column.
// Statuses: none (default) → requested → assigned → in_progress → resolved.
// Selecting "Requested" launches the existing "Let's Get This Deal"
// workflow — no new assistance system is introduced.
export const REP_ASSIST_STATUSES = [
  { key: 'none',        label: 'None',        tone: DEF   },
  { key: 'requested',   label: 'Requested',   tone: ACTION },
  { key: 'assigned',    label: 'Assigned',    tone: SCHED  },
  { key: 'in_progress', label: 'In Progress', tone: WAIT   },
  { key: 'resolved',    label: 'Resolved',    tone: DONE   },
];

export const REP_ASSIST_LABELS = Object.fromEntries(
  REP_ASSIST_STATUSES.map(s => [s.key, s.label])
);

export function repAssistStatus(client) {
  const raw = _norm(client?.rep_assist_status);
  if (!raw || raw === 'none') return REP_ASSIST_STATUSES[0];
  return REP_ASSIST_STATUSES.find(s => s.key === raw) || REP_ASSIST_STATUSES[0];
}
